import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, LogIn, UserPlus, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export const inputCls =
  "w-full border border-white/10 bg-[#0D1017] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-amber-600/60";
export const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500";
export const primaryBtn =
  "flex w-full items-center justify-center gap-3 bg-amber-600 py-4 font-mono text-sm uppercase tracking-[0.2em] text-black transition-colors duration-200 hover:bg-amber-500 disabled:opacity-60";

const MODES = [
  { key: "login", label: "Sign in", icon: LogIn },
  { key: "register", label: "Create account", icon: UserPlus },
];

export const PortalAuth = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else if (mode === "register") await register({ name: form.name, email: form.email, phone: form.phone || undefined, password: form.password });
      else {
        const { data } = await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin });
        setNotice(data.message);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="w-full max-w-md border border-white/10 bg-[#12151C] p-8 sm:p-10"
      data-testid="portal-auth-card"
    >
      <div className="font-display text-3xl tracking-[0.12em] text-slate-50">SAGITTARIUS</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Customer Portal</div>

      {mode !== "forgot" && (
        <div className="mt-8 flex border border-white/10" data-testid="portal-auth-tabs">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => { setMode(m.key); setError(""); }}
              data-testid={`portal-tab-${m.key}`}
              className={`flex flex-1 items-center justify-center gap-2 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${mode === m.key ? "bg-amber-600 text-black" : "text-slate-400 hover:text-slate-100"}`}
            >
              <m.icon size={12} /> {m.label}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-5" data-testid={`portal-${mode}-form`}>
        {mode === "register" && (
          <>
            <div>
              <label className={labelCls}>Full name</label>
              <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="portal-name-input" />
            </div>
            <div>
              <label className={labelCls}>Phone (as given to the workshop)</label>
              <input value={form.phone} onChange={set("phone")} placeholder="+64 21 000 0000" className={inputCls} data-testid="portal-phone-input" />
            </div>
          </>
        )}
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="portal-email-input" />
        </div>
        {mode !== "forgot" && (
          <div>
            <label className={labelCls}>Password{mode === "register" ? " (min 8 characters)" : ""}</label>
            <input type="password" required minLength={mode === "register" ? 8 : 1} value={form.password} onChange={set("password")} placeholder="••••••••" className={inputCls} data-testid="portal-password-input" />
          </div>
        )}

        {error && <div className="border border-red-500/50 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="portal-auth-error">{error}</div>}
        {notice && <div className="border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-300" data-testid="portal-auth-notice">{notice}</div>}

        <button type="submit" disabled={loading} className={primaryBtn} data-testid="portal-submit-button">
          {loading ? <Loader2 size={16} className="animate-spin" /> : mode === "forgot" ? <KeyRound size={16} /> : mode === "login" ? <LogIn size={16} /> : <UserPlus size={16} />}
          {mode === "login" ? "Sign in" : mode === "register" ? "Create my account" : "Send reset link"}
        </button>
      </form>

      <button
        type="button"
        onClick={() => { setMode(mode === "forgot" ? "login" : "forgot"); setError(""); setNotice(""); }}
        data-testid="portal-forgot-toggle"
        className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-amber-500"
      >
        {mode === "forgot" ? "← Back to sign in" : "Forgot password?"}
      </button>
    </motion.div>
  );
};
